import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Languages, LockKeyhole, Users } from "lucide-react";
import { IMAGES, VALUES } from "@/data/site";
import { Button } from "@/components/ui/button";
import { CTABand } from "@/components/CTABand";
import { PageHero } from "@/components/PageHero";
import { Reveal, ScaleIn } from "@/components/Motion";

const chapters = [
  { number: "01", title: "We listen before we prepare", text: "Immigration stories are personal. We begin by understanding your family, your documents, your concerns, and your desired outcome." },
  { number: "02", title: "We make complexity legible", text: "Checklists, plain-language explanations, and careful organization turn an intimidating process into manageable steps." },
  { number: "03", title: "We protect what you share", text: "Family records, identity documents, and immigration history are treated with discretion and respect." },
  { number: "04", title: "We serve the whole household", text: "Immigration, translation, notary, and tax needs often overlap. Egaal brings those services together in one trusted place." },
];

export default function About() {
  return (
    <>
      <PageHero
        eyebrow="About Egaal"
        titleLines={["A Trusted Office", "For Immigrant Families", "Moving Forward"]}
        description="Egaal Tax & Immigration Services combines professional document preparation with cultural understanding, confidentiality, and personalized attention for individuals and families throughout the United States."
        image={IMAGES.reunion}
        imageAlt="Family reunification and support"
      >
        <Reveal delay={0.62} className="mt-10">
          <Button asChild size="lg" className="rounded-full bg-brand-copper px-8 text-brand-navy hover:bg-brand-copper/90">
            <Link to="/contact" data-testid="about-hero-consultation-cta">Schedule a Consultation <ArrowRight className="h-4 w-4" /></Link>
          </Button>
        </Reveal>
      </PageHero>
      <section className="section-padding" data-testid="about-mission-section">
        <div className="section-shell grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
          <Reveal>
            <p className="overline text-brand-copper">Our Mission</p>
            <h2 className="mt-4 font-serif text-3xl font-bold sm:text-4xl lg:text-5xl">Helping families move forward with organized, human support.</h2>
          </Reveal>
          <Reveal delay={0.12} className="space-y-6 text-base leading-8 text-brand-navy/70">
            <p>
              Egaal was built around a simple belief: families deserve patient, professional support when navigating documents that shape their future. We help clients understand what is needed, gather it carefully, and prepare it with precision.
            </p>
            <p>
              Our office serves a diverse community with cultural awareness and multilingual understanding. We know immigration paperwork can feel stressful, so we make each step clearer, calmer, and easier to manage.
            </p>
          </Reveal>
        </div>
      </section>
      <section className="section-padding bg-white" data-testid="about-founder-section">
        <div className="section-shell grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          <ScaleIn className="relative">
            <div className="absolute -left-4 -top-4 h-full w-full rounded-lg bg-brand-copper/30" />
            <div className="image-spotlight relative overflow-hidden rounded-lg bg-brand-navy p-3 shadow-editorial">
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/wingman/8a65cc37-24ad-48e8-8010-e7d27078227c/attachments/efb1b74d763e48318ff4919339c4df35_Professional%20Photo.jpeg"
                alt="Khalid Ali, founder of Egaal Tax & Immigration Services"
                className="aspect-[4/5] w-full rounded-md object-cover object-center"
                data-testid="founder-photo"
              />
            </div>
            <div className="absolute -bottom-7 left-6 right-6 rounded-lg bg-brand-navy p-5 text-white shadow-editorial">
              <p className="font-serif text-2xl font-bold">Khalid Ali</p>
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-brand-copper">Founder, Egaal Tax & Immigration Services</p>
            </div>
          </ScaleIn>
          <Reveal delay={0.12} className="pt-8 lg:pt-0">
            <p className="overline text-brand-copper">Leadership</p>
            <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl" data-testid="founder-heading">Meet the Founder</h2>
            <div className="mt-8 border-l-4 border-brand-copper pl-6 sm:pl-8">
              <p className="text-base leading-8 text-brand-navy/72 sm:text-lg sm:leading-9">
                Khalid Ali is the founder of Egaal Tax & Immigration Services, bringing years of professional experience in legal support, immigration document preparation, tax services, and client advocacy. With a background in Business Administration and extensive experience working within the legal field, Khalid founded Egaal with a simple mission: to make complex immigration, tax, and administrative processes easier for individuals and families to navigate.
              </p>
              <p className="mt-6 text-base leading-8 text-brand-navy/72 sm:text-lg sm:leading-9">
                Known for his personalized and detail-oriented approach, Khalid is committed to providing professional, confidential, and dependable service while ensuring every client is treated with respect and care.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="section-padding bg-brand-navy text-white" data-testid="about-manifesto-section">
        <div className="section-shell">
          <Reveal className="mb-14 max-w-3xl">
            <p className="overline text-brand-copper">The Egaal Way</p>
            <h2 className="mt-4 font-serif text-3xl font-bold sm:text-4xl lg:text-5xl">Four commitments guide every consultation.</h2>
          </Reveal>
          <div className="grid gap-px overflow-hidden rounded-lg bg-white/10 md:grid-cols-2">
            {chapters.map((chapter, index) => (
              <Reveal key={chapter.number} delay={index * 0.08} className="bg-brand-navy p-8 lg:p-10">
                <p className="font-serif text-6xl font-black text-brand-copper/35">{chapter.number}</p>
                <h3 className="mt-8 font-serif text-2xl font-bold">{chapter.title}</h3>
                <p className="mt-4 text-sm leading-7 text-white/64">{chapter.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="section-padding bg-white" data-testid="about-values-section">
        <div className="section-shell grid items-center gap-14 lg:grid-cols-[1fr_0.9fr]">
          <div>
            <Reveal>
              <p className="overline text-brand-copper">How We Serve</p>
              <h2 className="mt-4 font-serif text-3xl font-bold sm:text-4xl">Professional standards with a community-centered approach.</h2>
            </Reveal>
            <div className="mt-10 grid gap-5 sm:grid-cols-2">
              {VALUES.map((value, index) => {
                const Icon = value.icon;
                return (
                  <Reveal key={value.title} delay={index * 0.06} className="rounded-lg border border-brand-navy/10 bg-brand-sand p-6">
                    <Icon className="h-6 w-6 text-brand-copper" />
                    <h3 className="mt-5 font-serif text-xl font-bold">{value.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-brand-navy/64">{value.description}</p>
                  </Reveal>
                );
              })}
            </div>
          </div>
          <ScaleIn className="relative">
            <img src={IMAGES.family} alt="Family supported by Egaal" className="aspect-[4/5] rounded-lg object-cover shadow-editorial" />
            <div className="absolute -bottom-8 left-6 right-6 rounded-lg bg-brand-cream p-6 shadow-editorial">
              <div className="flex items-center gap-4">
                <HeartHandshake className="h-8 w-8 text-brand-copper" />
                <p className="font-serif text-xl font-bold">Personal attention, not assembly-line service.</p>
              </div>
            </div>
          </ScaleIn>
        </div>
      </section>
      <section className="section-padding" data-testid="about-community-section">
        <div className="section-shell grid gap-6 md:grid-cols-3">
          {[
            { icon: Languages, title: "Culturally Informed", text: "We recognize the language, family, and cultural context behind every case." },
            { icon: LockKeyhole, title: "Confidential", text: "Sensitive documents and personal histories are handled with care and discretion." },
            { icon: Users, title: "Community Centered", text: "Our SeaTac office supports local families while assisting clients nationwide." },
          ].map((item, index) => {
            const Icon = item.icon;
            return (
              <ScaleIn key={item.title} delay={index * 0.08} className="rounded-lg bg-white p-8 text-center shadow-sm">
                <Icon className="mx-auto h-8 w-8 text-brand-copper" />
                <h3 className="mt-6 font-serif text-2xl font-bold">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-brand-navy/64">{item.text}</p>
              </ScaleIn>
            );
          })}
        </div>
      </section>
      <CTABand id="about" title="Experience personalized immigration document support from a team that understands families." />
    </>
  );
}
